import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Const from 'Utils/Consts'
import GetCarInfoPage from '../NewVisit/GetCarInfoPage'
import VisitOtherData from './VisitOtherData'
import { from } from 'jalali-moment'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  clearNewVisit,
  setCarInfo,
  setCustomerInfo,
  setDispatchId,
  setDraftVisitId,
} from 'Application/Visits/Actions/reducer'
import { useNavigate } from 'react-router-dom'
import { useSettings } from 'Contexts/SettingsContext'
import VisitsPaths from 'Application/Visits/Constants/VisitsPaths'
import { PersonTypeEnum } from 'Application/Visits/Actions/enums'
import { plateTypeFormat } from 'Application/Visits/Actions/plateType'
import gregorianDate from 'Utils/gregorianDate'
import { useAppSelector } from 'Store/selectors'
import { SetAssignVisitRequestCancelledByExpert } from 'Application/Visits/Actions/action'

interface DiaptchHolderInformationProps {
  data: any
}

const DiaptchHolderInformation: React.FC<DiaptchHolderInformationProps> = ({ data }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch<any>()
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstance = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const { settings } = useSettings()
  const storeFileType = settings?.REACT_APP_STORE_FILE_TYPE ?? Const.env.REACT_APP_STORE_FILE_TYPE

  const customerInfo = useAppSelector(s => s.visits.newVisit.customerInfo)

  const initializeMap = () => {
    if (!mapRef.current || !(window as any).L) return

    mapInstance.current = new (window as any).L.Map(mapRef.current, {
      key: Const.env.REACT_APP_NESHAN_MAP_API_KEY,
      maptype: 'dreamy',
      center: [data?.latitude ?? 35.6892, data?.longitude ?? 51.389],
      zoom: 17,
    })

    markerRef.current = (window as any).L.marker(
      [data?.latitude ?? 35.6892, data?.longitude ?? 51.389],
      {
        draggable: false,
      },
    ).addTo(mapInstance.current)

    return () => {
      mapInstance.current?.remove()
    }
  }

  useEffect(() => {
    initializeMap()

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove()
        mapInstance.current = null
      }
    }
  }, [])

  return (
    <div>
      <div className="border !border-black p-4 rounded-md flex flex-col gap-4">
        <div className="flex justify-between items-center gap-2">
          <span className="text-sm text-gray-600">{t('HOLDER_NATIONAL_CODE')}</span>
          <span className="text-sm font-semibold text-gray-800">{data?.nationalCode || '--'}</span>
        </div>

        <div className="flex justify-between items-center gap-2">
          <span className="text-sm text-gray-600">{t('HOLDER_FULLNAME')}:</span>
          <span className="text-sm font-semibold text-gray-800">
            {data?.fullName?.trim() ? data.fullName : '---'}
          </span>
        </div>

        <div className="flex justify-between items-center gap-2">
          <span className="text-sm text-gray-600">{t('HOLDER_PHONENUMBER')}:</span>
          <span className="text-sm font-semibold text-gray-800">
            {data?.mobilePhoneNumber || '---'}
          </span>
        </div>

        <div className="flex justify-between items-center gap-2">
          <span className="text-sm text-gray-600">{t('ADDRESS')}:</span>
          <span className="text-sm font-semibold text-gray-800">{data?.address}</span>
        </div>

        {data?.latitude && data?.longitude ? (
          <div className="border border-gray-300 border-dashed h-72 rounded-lg">
            <div ref={mapRef} style={{ width: '100%', height: '250px' }} />
          </div>
        ) : (
          'نقشه وجود ندارد'
        )}
      </div>

      <VisitOtherData data={data} isDispatch={true} />
      <br />
      <div className='flex flex-col gap-2'>
        <button
          className="btn btn-outline-primary btn-lg flex gap-1 items-center justify-center w-full"
          onClick={() => {
            dispatch(clearNewVisit(storeFileType))

            dispatch(setDispatchId(data.id))
            dispatch(
              setCarInfo({
                plate: {
                  carOtherNumber: data.plateCarOtherNumber,
                  carSerial: data.plateCarSerial,
                  char: data.plateChar,
                  cityCode: data.plateCityCode,
                  motorCycleOtherNumber: data.plateAdditionalNumber,
                  motorCycleSerial: data.plateMotorCycleSerial,
                  type: plateTypeFormat(data.plateType),
                },
                vehicleTypeId: data.vehicleTypeId,
                hasTrailer: null,
                vehicleBrandId: data.vehicleBrandId,
                vehicleTipId: data.vehicleTipId,
                vehicleColorId: data.vehicleColorId,
                vehicleUsageId: data.vehicleUsageId,
                vehicleStatus: null,
                numberOfCylinders: null,
                yearOfConstruction: data.yearOfConstruction,
                vehicleMileage: null,
                chassisNumber: data.chassisNumber,
                engineNumber: data.engineNumber,
                VIN: data.vin,
                cargoCompartmentId: data.cargoCompartmentId,
              }),
            )

            dispatch(
              setCustomerInfo({
                personType: data.personType !== null ? data.personType : null,
                isDeath: data.personType === PersonTypeEnum.REAL ? customerInfo.isDeath : false,
                firstName: data.firstName,
                lastName: data.personType === PersonTypeEnum.REAL ? data.lastName : '',
                identityCode: data.identityCode,
                birthDate:
                  data.personType === PersonTypeEnum.REAL
                    ? gregorianDate(new Date(data.birthDate))
                    : null,
                gender: data.gender,
                mobilePhoneNumber: data.contactPhone,
                visitLocationType: null,
                stateId: null,
                cityId: null,
                location: {
                  address: data.address,
                },
                branchCode: null,
              }),
            )

            navigate('/Visits/NewVisit')
          }}
        >
          {t('START_DRAFT_VISIT')}
        </button>

        {data.status !== 4 && (
          <button
            className="btn btn-error btn-lg flex gap-2 items-center justify-center w-full"
            onClick={async () => {
              try {
                await dispatch(
                  SetAssignVisitRequestCancelledByExpert({
                    data: {
                      visitRequestId: Number(data.id),
                    },
                    navigate,
                    t: t('EDIT_REQUEST'),
                  }),
                ).unwrap()
              } catch (error) {}
            }}
          >
            {t('CANCEL_EXPERTS')}
          </button>
        )}
      </div>
    </div>
  )
}

export default DiaptchHolderInformation
